/* eslint-disable react/no-unescaped-entities */
import {
    Button, Col, Form, Modal, notification, Popconfirm, Row, Space, Typography,
} from 'antd';
import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import { AiFillQuestionCircle } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';

import { closeModal } from '../../../actions/modals';
import { deleteRegion, editRegion } from '../../../api/regions';
import { checkRegionStatus } from '../../../screens/CommandCenter/utils/region';
import { ModalTypes } from '../../../types/modals';
import { getPathsFromPolygon } from '../../../utils/polygons';
import HelperIconTooltip from '../../HelperIconTooltip';
import ShowContent from './ShowContent';
import StatusIndicator from './StatusIndicator';

const { Title, Text } = Typography;

interface IRegionActionModal {
    onSuccess?: (p: any) => void
    onSuccessDelete?: (id: string) => void
}

const RegionActionModal = ({
    onSuccess,
    onSuccessDelete,
}: IRegionActionModal) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const [edit, setEdit] = useState(false);
    const [loading, setLoading] = useState(false);
    const [newPolygon, setNewPolygon] = useState<google.maps.Polygon>();
    const polygonRef = useRef<any>(null);

    const { activeModal, modalData } = useSelector((state: any) => state.modals);
    const isOpen = activeModal === ModalTypes.RegionActions;

    const onPolygonComplete = (polygon: google.maps.Polygon) => {
        console.log('onPolygonComplete polygon', polygon);
        newPolygon?.setMap(null);
        setNewPolygon(polygon);
    };

    const handleReset = () => {
        newPolygon?.setMap(null);
        setNewPolygon(undefined);
    };

    const handleCancel = () => {
        setEdit(false);
        handleReset();
        form.resetFields();
        polygonRef.current = null;
        dispatch(closeModal());
    };

    const handleCancelEdit = () => {
        setEdit(false);
        handleReset();
        form.resetFields();
    };

    const handleDelete = async () => {
        try {
            await deleteRegion(modalData.region._id);
            notification.success({
                message: 'Region deleted successfully',
            });

            onSuccessDelete && onSuccessDelete(modalData.region._id);
            handleCancel();
        } catch (error: any) {
            notification.error({
                message: 'Error',
                description: error.response.data.message,
            });
        }
    };

    const handleEdit = () => setEdit(true);

    const onSubmit = async (values: any) => {
        if (!edit) return handleCancel();

        setLoading(true);

        try {
            const coordinates = polygonRef.current ? getPathsFromPolygon(polygonRef.current) : modalData.region.geoLongLat.coordinates[0];

            await editRegion({
                ...values,
                region_id: modalData.region._id,
                coordinates,
                role: 'admin',
            });

            notification.success({
                message: 'Region Edited',
                description: 'Region edited successfully',
            });
            handleCancel();
            onSuccess && onSuccess(
                {
                    ...modalData.region,
                    ...values,
                    coordinates,
                },
            );
        } catch (error: any) {
            const { message } = error.response.data;
            notification.error({
                message: 'Error',
                description: message,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        form.resetFields();
    }, [modalData]);

    const statusColors = useMemo(
        () => modalData && checkRegionStatus(modalData.region.regionStatus),
        [modalData],
    );

    const EDIT_HELP = (
        <Text style={{ color: 'black' }}>
            <Text strong style={{ color: '#0092bb' }}>The region to edit</Text>
            {' '}
            is shown with the rest of the regions.
            If you need to change the shape of the region, you must click on
            <Text strong style={{ color: 'black' }}> 'Draw shape' </Text>
        </Text>
    );

    return (
        <Modal
            width="40%"
            visible={isOpen}
            title={(
                <Space align="center">
                    <Title style={{ margin: '0 8px 0 0' }} level={4}>
                        Region Detail:
                        {' '}
                        {modalData?.region?.regionName}
                    </Title>
                    {statusColors && <StatusIndicator stroke={statusColors.stroke} fill={statusColors.fill} size={20} />}
                    {edit && <HelperIconTooltip icon={<AiFillQuestionCircle size={20} />} text={EDIT_HELP} />}
                </Space>
            )}
            footer={null}
            onCancel={handleCancel}
        >

            <Form
                disabled={loading}
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                style={{ width: '100%' }}
            >
                {modalData
                    && (
                        <ShowContent
                            region={modalData.region}
                            allRegions={modalData.allRegions}
                            edit={edit}
                            onPolygonComplete={onPolygonComplete}
                            polygonRef={polygonRef}
                        />
                    )}
                <Row justify="end" style={{ marginTop: 16 }} gutter={16}>
                    {newPolygon
                        && (
                            <Col>
                                <Button type="primary" onClick={handleReset}>
                                    Clear Poligon
                                </Button>
                            </Col>
                        )}
                    {!edit && (
                        <Col>
                            <Popconfirm
                                title="Are you sure you want to delete this region? This action cannot be undone."
                                onConfirm={handleDelete}
                                okText="Yes"
                                cancelText="No"
                                okType="danger"
                            >
                                <Button type="primary" danger>
                                    Delete
                                </Button>
                            </Popconfirm>
                        </Col>
                    )}
                    <Col>
                        <Button type="primary" onClick={edit ? handleCancelEdit : handleEdit}>
                            {edit ? 'Cancel' : 'Edit'}
                        </Button>
                    </Col>
                    <Col>
                        <Button type="primary" htmlType="submit">
                            Ok
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default RegionActionModal;
